/**
 * BrainSwipe Game Page
 *
 * Основной экран игры с вопросами
 */

import { useSwipeStore } from '../stores/swipe';

export function BrainSwipeGame() {
  const {
    questions,
    currentIndex,
    session,
    selectedAnswerIndex,
    isAnswering,
    selectAnswer,
  } = useSwipeStore();

  const question = questions[currentIndex];

  if (!question) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-400 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Загрузка вопроса...</p>
        </div>
      </div>
    );
  }

  const streak = session?.currentStreak || 0;
  const points = session?.totalPoints || 0;

  // Multiplier display
  const getMultiplierInfo = (streak: number) => {
    if (streak >= 20) return { value: '5x', color: 'text-red-400', fire: '🔥🔥🔥' };
    if (streak >= 10) return { value: '3x', color: 'text-orange-400', fire: '🔥🔥' };
    if (streak >= 5) return { value: '2x', color: 'text-yellow-400', fire: '🔥' };
    return { value: '1x', color: 'text-white', fire: '' };
  };

  const multiplier = getMultiplierInfo(streak);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 via-purple-800 to-indigo-900 text-white flex flex-col">
      {/* Top bar */}
      <div className="p-4 flex justify-between items-center">
        <button
          onClick={() => window.location.href = "/swipe"}
          className="px-2 py-1 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-all"
        >
          ← Назад
        </button>
        <div className="flex items-center gap-2">
          <span className="text-xl">{question.categoryEmoji}</span>
          <span className="text-sm text-purple-300">{question.categoryName}</span>
        </div>

        <div className="flex items-center gap-4">
          {/* Streak */}
          <div className="flex items-center gap-1">
            <span className="text-lg">{multiplier.fire || '🎯'}</span>
            <span className={`font-bold ${multiplier.color}`}>
              {streak > 0 ? streak : ''}
            </span>
          </div>

          {/* Points */}
          <div className="flex items-center gap-1">
            <span>⚡</span>
            <span className="font-bold">{points}</span>
          </div>
        </div>
      </div>

      {/* Multiplier badge */}
      {streak >= 5 && (
        <div className="px-4 mb-2">
          <div className={`inline-block px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r ${
            streak >= 20 ? 'from-red-500 to-orange-500' :
            streak >= 10 ? 'from-orange-500 to-yellow-500' :
            'from-yellow-500 to-green-500'
          }`}>
            {multiplier.value} множитель!
          </div>
        </div>
      )}

      {/* Question */}
      <div className="flex-1 p-4 flex flex-col">
        <div className="bg-white/10 backdrop-blur rounded-2xl p-6 mb-4">
          {/* Course & Topic Badge */}
          <div className="mb-3 pb-3 border-b border-white/10">
            {question.collectionName ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{question.collectionIcon || '📚'}</span>
                  <span className="text-sm font-medium text-purple-200">{question.collectionName}</span>
                </div>
                <span className="text-xs text-purple-400">📖 {question.quizTopic}</span>
              </>
            ) : (
              <>
                <span className="text-xs text-purple-300">📚 Тема:</span>
                <span className="ml-2 text-sm font-medium text-purple-200">{question.quizTopic}</span>
              </>
            )}
          </div>

          <p className="text-lg leading-relaxed">{question.text}</p>

          {/* Question stats */}
          {question.totalAnswers && question.totalAnswers > 0 && (
            <div className="mt-3 text-xs text-purple-300">
              {Math.round((question.correctRate || 0) * 100)}% отвечают правильно
            </div>
          )}
        </div>

        {/* Options */}
        <div className="space-y-3 flex-1">
          {question.options.map((option: string, index: number) => {
            const isSelected = selectedAnswerIndex === index;
            const letters = ['A', 'B', 'C', 'D'];

            return (
              <button
                key={index}
                onClick={() => selectAnswer(index)}
                disabled={isAnswering || selectedAnswerIndex !== null}
                className={`w-full p-4 rounded-xl text-left transition-all flex items-center gap-3 ${
                  isSelected
                    ? 'bg-purple-500 ring-2 ring-purple-300 scale-[0.98]'
                    : 'bg-white/10 hover:bg-white/20 active:scale-[0.98]'
                } disabled:opacity-70`}
              >
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                  isSelected ? 'bg-white/20' : 'bg-white/10'
                }`}>
                  {letters[index]}
                </span>
                <span className="flex-1">{option}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="p-4 bg-black/20">
        <div className="flex justify-between text-sm text-purple-300">
          <span>Вопрос {currentIndex + 1}</span>
          <span>
            {session ? `${session.correctAnswers}/${session.questionsAnswered} правильно` : ''}
          </span>
        </div>
      </div>
    </div>
  );
}